import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { Search, Plus } from "lucide-react";
import { getAdminUsers } from "../../api/api";
import DataTable from "../../components/DataTable";
import UserModal from "../../components/UserModal";
import GeneratedCredentialsModal from "../../components/GeneratedCredentialsModal";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";

const ROLE_LABEL = { "super-admin": "Super Admin", admin: "Admin", caredesk: "Care Desk", finance: "Finance", master: "Master", technician: "Technician" };

export default function UserMaster() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [credentialsModal, setCredentialsModal] = useState({ open: false, credentials: [] });
  const currentUser = useSelector((s) => s.users.user);

  const load = () => {
    setLoading(true);
    getAdminUsers({ search: search || undefined, page, limit: 15 })
      .then((res) => {
        setUsers(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage });
      })
      .catch((err) => toast.error(err?.message || "Could not load users."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page]);

  const columns = [
    {
      key: "name",
      header: "User",
      render: (u) => (
        <div>
          <p className="font-medium">{u.name}</p>
          <p className="text-xs text-muted-foreground">{u.email}</p>
        </div>
      ),
    },
    {
      key: "roles",
      header: "Roles",
      render: (u) => (
        <div className="flex flex-wrap gap-1">
          {u.roles.map((r) => (
            <Badge key={r} variant={r === "super-admin" ? "default" : "outline"}>
              {ROLE_LABEL[r] || r}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (u) => <Badge variant={u.status === "active" ? "secondary" : "destructive"}>{u.status}</Badge>,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">User Master</h2>
          <p className="text-sm text-muted-foreground">
            Manage staff dashboard accounts and their roles — doctor logins are managed from Doctors instead
          </p>
        </div>
        <Button onClick={() => { setEditingUser(null); setModalOpen(true); }}>
          <Plus className="h-4 w-4" /> Add user
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by name, email, or phone…"
          className="pl-9"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      <DataTable
        columns={columns}
        data={users}
        loading={loading}
        emptyMessage="No users found."
        page={page}
        numberOfPage={meta.numberOfPage}
        onPageChange={setPage}
        onRowClick={(u) => { setEditingUser(u); setModalOpen(true); }}
        rowKey={(u) => u._id}
      />

      <UserModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        user={editingUser}
        currentUserId={currentUser?._id}
        onSaved={load}
        onPasswordReset={(cred) => setCredentialsModal({ open: true, credentials: [cred] })}
      />

      <GeneratedCredentialsModal
        open={credentialsModal.open}
        onOpenChange={(open) => setCredentialsModal((c) => ({ ...c, open }))}
        credentials={credentialsModal.credentials}
      />
    </div>
  );
}
